import { Loading } from "components/ui/Loading/Loading";
import {
  type ThreadMessageItem,
  useEmailThread,
} from "hooks/use-email-thread-api";
import { cn } from "lib/utils";
import { Inbox, Maximize2, Send } from "lucide-react";
import { type FC, useEffect, useRef, useState } from "react";
import { formatShortDateTime } from "utils/relative-time";

import { EmailThreadMessageModal } from "./EmailThreadMessageModal";

export type EmailThreadViewProps = {
  providerThreadId: string;
  onReply?: (item: ThreadMessageItem) => void;
};

/**
 * メッセージ一意キーを返す
 * @param {ThreadMessageItem} item メッセージ
 * @return {string} key
 */
function messageKey(item: ThreadMessageItem): string {
  return (
    item.sentEmailId ??
    item.replyEmailId ??
    `${item.direction}-${item.occurredAt}`
  );
}

/**
 * スレッドビュー (Gmail 風アコーディオン).
 *   - 最新 1 件は自動展開、それ以外は折り畳み
 *   - 折り畳み時はヘッダ + 本文 1 行プレビュー (truncate)
 *   - 各メッセージに拡大モーダルボタンを表示
 *   - 送信は薄紫 (violet)、受信は中立 (slate) 配色
 *
 * @param {EmailThreadViewProps} props providerThreadId と任意 onReply
 * @return {JSX.Element} スレッドビュー
 */
export const EmailThreadView: FC<EmailThreadViewProps> = ({
  providerThreadId,
  onReply,
}) => {
  const { thread, loading, error } = useEmailThread({ providerThreadId });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modalMessage, setModalMessage] = useState<ThreadMessageItem | null>(
    null,
  );
  const initedThreadIdRef = useRef<string | null>(null);

  // 別スレッドへ切替 + 初回ロード完了時のみ「最新 1 件展開」 で初期化.
  // 同一スレッドの refetch (送信後など) ではユーザの開閉状態を保護する.
  useEffect(() => {
    if (!thread || thread.items.length === 0) return;
    if (initedThreadIdRef.current === providerThreadId) return;
    initedThreadIdRef.current = providerThreadId;
    const latestKey = messageKey(thread.items[thread.items.length - 1]!);
    setExpanded(new Set([latestKey]));
  }, [providerThreadId, thread]);

  /**
   * メッセージの開閉をトグルする
   * @param {string} key メッセージキー
   * @return {void}
   */
  const toggle = (key: string): void => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loading size="sm" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-rose-600 py-2">
        スレッドの取得に失敗しました
      </div>
    );
  }
  if (!thread || thread.items.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-2">
        このスレッドには他のメッセージがありません
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-700">
        このスレッドの会話 ({thread.items.length})
      </h3>
      <ul className="space-y-2">
        {thread.items.map(item => {
          const key = messageKey(item);
          const isOpen = expanded.has(key);
          return (
            <ThreadMessageRow
              key={key}
              item={item}
              isOpen={isOpen}
              onToggle={() => toggle(key)}
              onExpand={() => setModalMessage(item)}
              onReply={onReply ? () => onReply(item) : null}
            />
          );
        })}
      </ul>
      <EmailThreadMessageModal
        isOpen={modalMessage !== null}
        message={modalMessage}
        onClose={() => setModalMessage(null)}
      />
    </div>
  );
};

type ThreadMessageRowProps = {
  item: ThreadMessageItem;
  isOpen: boolean;
  onToggle: () => void;
  onExpand: () => void;
  /** 受信メッセージへの返信 (null のとき非表示) */
  onReply: (() => void) | null;
};

/**
 * スレッド内 1 メッセージ. クリックで開閉、Maximize2 で詳細モーダル.
 * @param {ThreadMessageRowProps} props メッセージと開閉状態とハンドラ
 * @return {JSX.Element} メッセージ行
 */
const ThreadMessageRow: FC<ThreadMessageRowProps> = ({
  item,
  isOpen,
  onToggle,
  onExpand,
  onReply,
}) => {
  const isSent = item.direction === "sent";
  const previewLine = item.bodyText?.split("\n").find(l => l.trim()) ?? "";

  return (
    <li
      className={cn(
        "rounded-lg border text-xs",
        isSent
          ? "border-violet-200 bg-violet-50"
          : "border-slate-200 bg-slate-50",
      )}
    >
      <div className="flex items-center gap-2 p-3">
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          {isSent ? (
            <Send
              size={12}
              className="text-violet-700 shrink-0"
              aria-label="送信"
            />
          ) : (
            <Inbox
              size={12}
              className="text-slate-500 shrink-0"
              aria-label="受信"
            />
          )}
          <span className="font-medium truncate shrink-0">
            {isSent
              ? `${item.fromName || "自分"} → ${
                  item.toEmails.join(", ") || "(宛先不明)"
                }`
              : `${item.fromName || item.fromEmail} → 自分`}
          </span>
          {!isOpen && previewLine && (
            <span className="text-slate-500 truncate min-w-0">
              {previewLine}
            </span>
          )}
          <span className="ml-auto text-slate-500 font-mono text-[10px] shrink-0">
            {formatShortDateTime(item.occurredAt)}
          </span>
        </button>
        <button
          type="button"
          className="text-slate-500 hover:text-slate-700 shrink-0"
          onClick={onExpand}
          aria-label="拡大表示"
          title="拡大表示"
        >
          <Maximize2 size={12} aria-hidden />
        </button>
        {!isSent && onReply && (
          <button
            type="button"
            className="text-sky-600 hover:underline text-[10px] shrink-0"
            onClick={onReply}
          >
            返信
          </button>
        )}
      </div>

      {isOpen && (
        <div className="px-3 pb-3 -mt-1">
          <div className="font-medium mb-1 break-words">
            {item.subject || "(件名なし)"}
          </div>
          {item.bodyText && (
            <pre className="whitespace-pre-wrap text-[11px] text-slate-700">
              {item.bodyText}
            </pre>
          )}
        </div>
      )}
    </li>
  );
};
